/** @format */
import { useRouter } from 'next/router';
import React, { useContext, useEffect, useState } from 'react';

import { NavigationLinks } from '@toucan/common/services/navigation/types';

import {
    ApplicationContext,
    MediaQuery,
    useMediaQuery,
} from '@toucan/client/global/application';

import { DesktopMainNav } from '../components/desktop-main-nav';
import { Drawer, DrawerOpenDirection } from '../components/drawer';
import { MobileMainNav } from '../components/mobile-main-nav';

export const useMenu = function (navigationLinks: NavigationLinks) {
    const [isOpen, setIsOpen] = useState(false);
    const [shouldSkipAnimation, setShouldSkipAnimation] = useState(false);
    const isSmallTablet = useMediaQuery(MediaQuery.SMALL_TABLET);
    const { setBlackoutVisible, modal } = useContext(ApplicationContext);
    const menuContent = isSmallTablet ? (
        <MobileMainNav navigationLinks={navigationLinks} />
    ) : (
        <DesktopMainNav navigationLinks={navigationLinks} />
    );

    const router = useRouter();

    useEffect(() => {
        const handleRouteChangeStart = () => {
            setShouldSkipAnimation(true);
            setIsOpen(false);
        };

        router.events.on('routeChangeStart', handleRouteChangeStart);

        return () => {
            router.events.off('routeChangeStart', handleRouteChangeStart);
        };
    }, [router.events]);

    function setMenuVisible(isVisible: boolean) {
        setIsOpen(isVisible);
        setShouldSkipAnimation((prevState) => {
            return isVisible ? false : prevState;
        });
        setBlackoutVisible(isVisible);
    }

    useEffect(() => {
        // ensures that if the modal has been opened then we close the menu
        if (isOpen && modal.visible) {
            setMenuVisible(false);
            setBlackoutVisible(true);
        }
    }, [modal.visible, isOpen]);

    const menu = (
        <Drawer
            isOpen={isOpen}
            immediate={shouldSkipAnimation}
            data-testid={`drawer__${isOpen ? 'open' : 'closed'}`}
            openDirection={DrawerOpenDirection.LEFT}
            onClose={() => setMenuVisible(false)}
        >
            {menuContent}
        </Drawer>
    );

    return { menu, setMenuVisible };
};
